import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S, setStatusColors } from './styles';
import { API, fmtTime, authHeaders, startLightspeedConnect, onSessionProblem, roleLabel } from './utils/helpers';
import { groupsFor } from './components/settings/groups';
import ServiceQueue from './components/ServiceQueue';
import Quotes from './components/Quotes';
import PickupQueue from './components/PickupQueue';
import Customers from './components/Customers';
import Settings from './components/Settings';
import TechView from './components/TechView';
import Messages from './components/Messages';
import Bookings from './components/Bookings';
import Payments from './components/Payments';
import Login from './components/Login';
import SpinboardMark from './components/SpinboardMark';
import ResetPassword from './components/ResetPassword';
import BookingPage from './components/BookingPage';
import AdminConsole from './components/AdminConsole';
import IntakeForm from './components/IntakeForm';
import BookingManagePage from './components/BookingManagePage';
import PaymentPage from './components/PaymentPage';
import RentalHoldPage from './components/RentalHoldPage';
import QuotePage from './components/QuotePage';
import FeedbackPage from './components/FeedbackPage';
import Reviews from './components/Reviews';
import PartsToOrder from './components/PartsToOrder';
import Toaster from './components/Toaster';
import GettingStarted from './components/GettingStarted';

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

// How often the app-wide poller (badges, chime, message list) runs. See the
// scheduler in App() for why a hidden tab slows down rather than stopping.
const POLL_VISIBLE_MS = 15 * 1000;
const POLL_HIDDEN_MS = 60 * 1000;

export default function App() {
  const [authStatus, setAuthStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const token = localStorage.getItem('sb_token');
      const user = JSON.parse(localStorage.getItem('sb_user'));
      if (!token || !user || isTokenExpired(token)) {
        localStorage.removeItem('sb_token');
        localStorage.removeItem('sb_user');
        return null;
      }
      return user;
    } catch { return null; }
  });

  function handleLogin(user) {
    setCurrentUser(user);
    if (user.preferences?.theme) {
      setTheme(user.preferences.theme);
      localStorage.setItem('vsm-theme', user.preferences.theme);
    }
    setNav('queue');
    setSessionIssue(null);
  }

  function handleLogout() {
    localStorage.removeItem('sb_token');
    localStorage.removeItem('sb_user');
    setCurrentUser(null);
    setSessionIssue(null);
  }

  // Set when a staff route returns 401 while the token still looks valid -
  // requireAuth failing closed, a deleted user, or the database being briefly
  // unreachable. Distinct from an expired token, which just signs out.
  const [sessionIssue, setSessionIssue] = useState(null);

  // One interceptor, installed once, covering every fetch in the app.
  // See the note above onSessionProblem in utils/helpers.js.
  useEffect(() => {
    onSessionProblem((status) => {
      const token = localStorage.getItem('sb_token');
      if (!token) return;
      if (status === 503) {
        // The server reached its own conclusion: it cannot get to the database.
        // Nothing is wrong with this account, so signing out would be both
        // wrong and destructive.
        setSessionIssue('unavailable');
        return;
      }
      if (isTokenExpired(token)) {
        // Nothing ambiguous about this one - the shift ended. Sign out and let
        // the login screen say so, rather than waiting up to five minutes for
        // the periodic expiry check below to notice.
        localStorage.removeItem('sb_token');
        localStorage.removeItem('sb_user');
        setCurrentUser(null);
        setSessionIssue(null);
      } else {
        // The token is fine, so this is the server declining to verify the
        // account. That can be a real revocation or a passing database blip,
        // and we cannot tell which from here - so do not destroy what the
        // person was in the middle of. Ask them.
        setSessionIssue('unverified');
      }
    });
  }, []);
  // ?open=parts: the link in the "parts to order" email (Quotes, 24 Oct).
  const [nav, setNav] = useState(() => {
    // ?open=parts from the Parts to Order email, ?open=reviews from a low-score alert.
    try { const o = new URLSearchParams(window.location.search).get('open'); return o === 'parts' || o === 'reviews' ? o : 'queue'; } catch (_) { return 'queue'; }
  });
  // The owner's setup guide (18 Oct): shown until the shop is set up, and the
  // screen an owner lands on until then. `guideReturn` is the note shown after
  // Stripe sends them back.
  const [guide, setGuide] = useState(null);
  const [guideReturn, setGuideReturn] = useState(null);
  const guideLanded = useRef(false);
  // Settings is the one module with sub-items in the sidebar, so the open group
  // is a navigation position and lives here with `nav`.
  const [settingsGroup, setSettingsGroup] = useState(null);
  // The id of the Settings group holding unsaved edits, or null. Reported up by
  // SettingsPage; drives the dot in the sidebar and the dialog below.
  const [dirtyGroup, setDirtyGroup] = useState(null);
  // The Settings group list in the sidebar folds away when Settings is
  // clicked while already open (Manny, 26 Oct). Remembered for the session.
  const [settingsFolded, setSettingsFolded] = useState(() => {
    try { return sessionStorage.getItem('sb_settings_folded') === '1'; } catch (_) { return false; }
  });
  const foldSettings = (v) => {
    setSettingsFolded(v);
    try { sessionStorage.setItem('sb_settings_folded', v ? '1' : '0'); } catch (_) {}
  };
  // Where a click wanted to go while something was unsaved: { nav, group }.
  const [pendingNav, setPendingNav] = useState(null);
  // Bumped to ask SettingsPage to save. A counter rather than a callback so
  // App never has to hold a reference into that component.
  const [saveSignal, setSaveSignal] = useState(0);
  // Where to go once the save lands. Held until SettingsPage reports itself
  // clean: a failed save keeps the person where they are, with the red line
  // and their edits, rather than moving on and losing them.
  const [leaveAfterSave, setLeaveAfterSave] = useState(null);
  // Preference order: account (persisted in sb_user.preferences.theme from last
  // login), then per-device localStorage, then dark.
  const [theme, setTheme] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('sb_user'));
      if (u?.preferences?.theme) return u.preferences.theme;
    } catch {}
    return localStorage.getItem('vsm-theme') || 'dark';
  });
  const [shopProfile, setShopProfile] = useState({});
  const [pendingCustomer, setPendingCustomer] = useState(null);
  const [pendingQuote, setPendingQuote] = useState(null); // { id, isNew } from 'spinboard-nav'
  const [quotesHome, setQuotesHome] = useState(0);   // bumps when Quotes is pressed in the sidebar
  const clearPendingQuote = useCallback(() => setPendingQuote(null), []);
  const [techViewNavHandler, setTechViewNavHandler] = useState(null);

  // ── Unread messages badge + chime (visible/audible from any module) ────────
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  // The latest conversation list from the one app-wide poller. null until the
  // first poll lands. Messages.jsx renders from it instead of polling itself.
  const [polledConversations, setPolledConversations] = useState(null);
  const [hasFailedMessages, setHasFailedMessages] = useState(false);
  const [newBookingCount, setNewBookingCount] = useState(0);
  const [newPaymentCount, setNewPaymentCount] = useState(0);
  // Customers' answers to quotes that nobody has opened yet. Shop-wide, not
  // per user: an approval needs someone to act, and it leaves the badge when
  // anyone opens that quote (PUT /api/quotes/:id/seen).
  const [quoteAnswerCount, setQuoteAnswerCount] = useState(0);
  const lastQuoteAnswerRef = useRef(null);
  // Parts to Order (23 Oct): how many parts need ordering. Read from the
  // server's cached list only; null until the list has been worked out once.
  const [partsOrderCount, setPartsOrderCount] = useState(null);
  // Reviews (25 Oct): low scores (0 to 6) nobody has logged a call for yet.
  // `reviewsActive`: the item only appears once the shop has asked a customer.
  const [reviewsCount, setReviewsCount] = useState(0);
  const [reviewsActive, setReviewsActive] = useState(false);
  // Settings › Website, only once Spinboard has switched it on for this shop
  // (3 Nov, server/helpers/websiteFeature.js). Asked once per sign-in.
  const [websiteOn, setWebsiteOn] = useState(false);
  useEffect(() => {
    if (!['owner', 'manager'].includes(currentUser?.role)) { setWebsiteOn(false); return undefined; }
    let cancelled = false;
    fetch(`${API}/api/website/admin/site-status`, { credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('sb_token')}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) setWebsiteOn(!!(d && d.available)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.id, currentUser?.role]);
  useEffect(() => {
    const done = () => setReviewsCount(n => Math.max(0, n - 1));
    window.addEventListener('spinboard-review-followed-up', done);
    return () => window.removeEventListener('spinboard-review-followed-up', done);
  }, []);
  // Quotes.jsx says when someone opened an answer, so the badge drops now
  // rather than on the next poll.
  useEffect(() => {
    const seen = () => {
      setQuoteAnswerCount(n => Math.max(0, n - 1));
      if (lastQuoteAnswerRef.current) lastQuoteAnswerRef.current -= 1;
    };
    window.addEventListener('spinboard-quote-answer-seen', seen);
    return () => window.removeEventListener('spinboard-quote-answer-seen', seen);
  }, []);
  const audioCtxRef = useRef(null);
  const lastSeenRef = useRef({}); // phone -> ISO timestamp of last-seen activity
  const hasPolledOnceRef = useRef(false); // suppress chime for pre-existing unread on load
  // Last payment count this session saw, so the chime fires on the TRANSITION
  // (2 -> 3) rather than on every poll where the count is simply non-zero.
  // null means "haven't polled yet" and is distinct from 0 — without that
  // distinction, logging in with one unseen payment already waiting would
  // chime, which is the same mistake hasPolledOnceRef exists to prevent for
  // messages.
  const lastPaymentCountRef = useRef(null);
  // The server-side "last looked at Payments" watermark, kept fresh by the
  // poll, plus the copy frozen at the moment this user walks into the module.
  //
  // Two values rather than one, because opening Payments moves the watermark
  // to NOW — so by the time the table renders, the live watermark says nothing
  // is new and the "New" badges would never appear at all. paymentsNewSince is
  // the snapshot taken one instant before that, and it deliberately does NOT
  // track the poll: the badges have to survive the whole visit.
  const paymentsViewedAtRef = useRef(null);
  const [paymentsNewSince, setPaymentsNewSince] = useState(null);

  // A trimmed theme: only the colours the shell itself draws with. Every name
  // used as T.<something> in this file must exist in BOTH branches, or it is
  // silently `undefined` -- and an undefined background is a transparent one,
  // not an error. That is how the unsaved-changes dialog shipped with no panel
  // behind it on 17 Sep, reading `card` and `cardBorder`, which live in
  // theme.js and were never here. check-app-theme.js now refuses that.
  const T = theme === 'light' ? {
    bg: '#f0f0eb', sidebar: '#ffffff', border: '#e0e0d8',
    text: '#1a1a1a', textSub: '#777',
    navActive: '#1a2336', navBorder: '#e67e22',
    btnPrimary: '#1a2336', btnPrimaryText: '#ffffff',
    filterBorder: '#d0d0c8',
    card: '#ffffff', cardBorder: '#e0e0d8',
  } : {
    bg: '#111827', sidebar: '#1a2336', border: '#243050',
    text: '#e8e4dc', textSub: '#5a6a8a',
    navActive: '#e8e4dc', navBorder: '#e67e22',
    btnPrimary: '#e67e22', btnPrimaryText: '#ffffff',
    filterBorder: '#2e3d58',
    card: '#1a2336', cardBorder: '#243050',
  };

  // Depends on currentUser, not []: /api/settings is auth-gated, so on a fresh
  // login this effect has already run (and 401'd) by the time a token exists.
  // Mounting with a stored token still fetches once, as before.
  // Status colors come from Lightspeed, not a hardcoded map — loaded once here
  // so every statusPill in the app renders the shop's real palette. Failure is
  // non-fatal: statusPill falls back to its literal map.
  useEffect(() => {
    if (!currentUser) return;
    fetch(`${API}/api/tickets/status-meta`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(meta => setStatusColors(Object.fromEntries(
        (meta || []).filter(m => m?.name).map(m => [m.name.toLowerCase(), m.htmlColor])
      )))
      .catch(() => {});
  }, [currentUser]);

  // The owner's setup guide. Loaded at sign-in; while setup is unfinished the
  // owner lands on it, unless they chose "I will finish later" in the last day.
  // Stripe sends the owner back to /settings?stripe=..., which nothing else on
  // the main screen reads, so the result is shown on the guide.
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'owner') { setGuide(null); return; }
    const params = new URLSearchParams(window.location.search);
    const stripe = params.get('stripe');
    if (stripe) {
      setGuideReturn(stripe === 'connected'
        ? { ok: true, text: 'Stripe is connected. Customers can now pay deposits and payment links online.' }
        : { ok: false, text: `Stripe did not finish connecting (${params.get('reason') || 'no reason given'}). Press Connect Stripe to try again, or reply to your welcome email and we will help.` });
      window.history.replaceState({}, '', '/');
    }
    fetch(`${API}/api/getting-started`, { credentials:'include', headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(g => {
        if (!g) return;
        setGuide(g);
        if (guideLanded.current) return;
        guideLanded.current = true;
        let snoozed = false;
        try { snoozed = Number(localStorage.getItem('sb_guide_snooze') || 0) > Date.now(); } catch (_) {}
        // A link that names a screen (?open=parts, from an email) wins.
        if (stripe || (!g.complete && !snoozed && !params.get('open'))) setNav('start');
      })
      .catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    fetch(`${API}/api/settings`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(s => {
        if (s.shopProfile) setShopProfile(s.shopProfile);
        // Theme is deliberately NOT read from settings. `settings` is a single
        // shared row, so reading it here meant the last person to toggle set
        // the theme for the entire shop and silently overrode everyone else's
        // choice. It lives in localStorage, per person, per device.
      })
      .catch(() => {});
  }, [currentUser]);

  // Listen for internal navigation events (e.g. from CustomerPopover)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.tab) {
        setNav(e.detail.tab);
        if (e.detail.customerId) {
          setPendingCustomer({ id: e.detail.customerId, name: e.detail.customerName || '' });
        }
        // Quotes (23 Oct): open one quote, or the New Quote form.
        if (e.detail.quoteId || e.detail.newQuote) {
          setPendingQuote({ id: e.detail.quoteId || null, isNew: !!e.detail.newQuote });
        }
      }
    };
    window.addEventListener('spinboard-nav', handler);
    return () => window.removeEventListener('spinboard-nav', handler);
  }, []);

  // Spinboard's own bill (22 Oct): a failed payment shows a warning naming the
  // day the shop goes read-only, then a read-only banner (server/helpers/
  // suspension.js). Asked with the auth status, so it follows the same rhythm.
  const [billingState, setBillingState] = useState({ state: 'ok' });
  const checkBilling = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/billing-portal/status`, { credentials:'include', headers: authHeaders() });
      if (r.ok) setBillingState(await r.json());
    } catch (_) { /* the banner is a courtesy; the server enforces read-only regardless */ }
  }, []);
  const openBilling = async () => {
    try {
      const r = await fetch(`${API}/api/billing-portal`, { method:'POST', credentials:'include', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) throw new Error(d.error || 'Could not open the billing page.');
      window.location.href = d.url;
    } catch (e) { alert(e.message); }
  };

  const checkAuth = useCallback(async () => {
    checkBilling();
    try {
      const res = await fetch(`${API}/api/auth-status`, { credentials:'include', headers: authHeaders() });
      // A 401 means "nobody is asking", NOT "the shop has no Lightspeed". The
      // body of a 401 has no `authenticated` field, so parsing it as a status
      // produced authenticated:false and lit the "Not connected to Lightspeed"
      // banner over a perfectly connected shop.
      //
      // `unknown` rather than a bare false, because THIS STATE STILL HAS TO
      // RENDER. `if (!authStatus)` gates the whole app on a null — including the
      // login screen — so returning here without setting anything left a signed
      // out user on "Loading…" with no way back in. A null is not a safe
      // "haven't answered yet"; it is a lock.
      if (res.status === 401) {
        setAuthStatus({ authenticated: false, unknown: true });
        return false;
      }
      const data = await res.json();
      setAuthStatus(data);
      return data.authenticated;
    } catch {
      setAuthStatus({ authenticated: false });
      return false;
    }
  }, [checkBilling]);

  // Depends on currentUser, not []: /api/auth-status became auth-gated in phase
  // 3c-2, so on a fresh login this effect had already run — and 401'd — before a
  // token existed, and nothing re-ran it for the rest of the session. The shop
  // was connected, the queue loaded from Lightspeed, and the banner sat there
  // saying it was not. A reload fixed it, which is exactly why it survived
  // testing: anyone with a stored token never saw it.
  //
  // This is the SECOND time gating a route did this (see the /api/settings
  // effects above, Aug 12). The pattern is the trap: gating a route is not a
  // server-only change when the client calls it before it has a token.
  useEffect(() => { checkAuth(); }, [checkAuth, currentUser]);

  // Re-ask every five minutes while someone is signed in, so a connection that
  // stops working mid-shift shows up as a banner instead of a queue that just
  // goes quiet. Cheap: the server answers from calls it already made and makes
  // at most one small Lightspeed call per five minutes per shop.
  useEffect(() => {
    if (!currentUser) return undefined;
    const id = setInterval(() => { checkAuth(); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [checkAuth, currentUser]);

  // Periodic token expiry check — logs out automatically when token expires mid-session
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('sb_token');
      if (token && isTokenExpired(token)) {
        localStorage.removeItem('sb_token');
        localStorage.removeItem('sb_user');
        setCurrentUser(null);
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Browsers block script-triggered audio until the user has interacted with
  // the page at least once. This one-time listener "unlocks" a shared
  // AudioContext on the first click anywhere, so the chime works for the
  // rest of the session without needing a dedicated button.
  useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        try {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (_) { /* Web Audio unsupported — chime silently unavailable */ }
      } else if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      window.removeEventListener('click', unlock);
    };
    window.addEventListener('click', unlock);
    return () => window.removeEventListener('click', unlock);
  }, []);

  // Short synthesized two-tone chime — no audio file to host or upload.
  // Takes the two tones so a second kind of event can be told apart by ear:
  // messages keep the original rising pair, money uses a lower, longer pair
  // (see playPaymentChime below). Anyone at the counter should be able to tell
  // "someone texted" from "someone just paid" without looking up.
  const playChime = useCallback((freqs = [880, 1108]) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return; // not yet unlocked by a user gesture
    try {
      const now = ctx.currentTime;
      freqs.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = now + i * 0.12;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.15, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.3);
      });
    } catch (_) {}
  }, []);

  // A distinctly lower, more "settled" pair for money arriving.
  const playPaymentChime = useCallback(() => playChime([523, 784]), [playChime]);

  // Polls conversations independently of whether the Messages tab is active,
  // so the nav badge and chime work from any module. Chimes only when a
  // conversation's most recent message is inbound AND newer than what was
  // seen on the previous poll — never on Manny's own outbound sends, and
  // never on the very first poll of a session (which would otherwise chime
  // once for every pre-existing unread conversation on load).
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const poll = () => {
      const token = localStorage.getItem('sb_token');
      if (!token) return;

      // New-booking badge rides the same 15s cycle. Per-user: the count is
      // measured against this staffer's own bookings_viewed_at, so one person
      // opening Bookings doesn't clear it for anyone else.
      fetch(`${API}/book/staff/new-count`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(d => { if (!cancelled) setNewBookingCount(d.count || 0); })
        .catch(() => {});

      // Payments received since this user last opened the Payments module.
      // Same 15s cycle, same per-user watermark as bookings. Chimes on the
      // count going UP, so a payment landing while Manny is on another screen
      // is heard as well as seen — a customer paying their invoice is the
      // event that most often needs someone to do something next.
      fetch(`${API}/api/payments/new-count`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(d => {
          if (cancelled) return;
          const n = d.count || 0;
          const prev = lastPaymentCountRef.current;
          setNewPaymentCount(n);
          paymentsViewedAtRef.current = d.viewedAt || null;
          if (prev !== null && n > prev) playPaymentChime();
          lastPaymentCountRef.current = n;
        })
        .catch(() => {});

      // Same cycle: quotes the customer has answered. Chimes when one comes in.
      if (currentUser?.permissions?.quotes !== false) {
        fetch(`${API}/api/quotes/answers-count`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => r.json())
          .then(d => {
            if (cancelled || typeof d.count !== 'number') return;
            const prev = lastQuoteAnswerRef.current;
            setQuoteAnswerCount(d.count);
            if (prev !== null && d.count > prev) playPaymentChime();
            lastQuoteAnswerRef.current = d.count;
          })
          .catch(() => {});
      }

      if (currentUser?.permissions?.reviews) {
        fetch(`${API}/api/feedback/attention-count`, { credentials: 'include', headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(d => { if (!cancelled && d && typeof d.count === 'number') { setReviewsCount(d.count); setReviewsActive(!!d.active); } })
          .catch(() => {});
      }

      if (currentUser?.permissions?.parts_order !== false) {
        fetch(`${API}/api/parts-order/count`, { credentials: 'include', headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(d => { if (!cancelled && d && (typeof d.count === 'number' || d.count === null)) setPartsOrderCount(d.count); })
          .catch(() => {});
      }

      fetch(`${API}/api/sms/conversations`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(list => {
          if (cancelled || !Array.isArray(list)) return;

          // Handed to Messages.jsx, which no longer polls this route itself.
          // Replaced only when the content changed, so an unchanged poll does
          // not re-render the Messages screen every cycle.
          setPolledConversations(prev =>
            (prev && JSON.stringify(prev) === JSON.stringify(list)) ? prev : list);

          const unreadConvos = list.filter(c => Number(c.unread_count || 0) > 0).length;
          setUnreadMsgCount(unreadConvos);
          setHasFailedMessages(list.some(c => c.has_failed));

          if (hasPolledOnceRef.current) {
            const hasNewInbound = list.some(c => {
              if (c.last_direction !== 'inbound') return false;
              const prevSeen = lastSeenRef.current[c.customer_phone];
              return !prevSeen || new Date(c.last_created_at) > new Date(prevSeen);
            });
            if (hasNewInbound) playChime();
          }

          const nextSeen = {};
          list.forEach(c => { nextSeen[c.customer_phone] = c.last_created_at; });
          lastSeenRef.current = nextSeen;
          hasPolledOnceRef.current = true;
        })
        .catch(() => {});
    };

    // THE conversations poller for the whole app (26 Sep). Messages.jsx used to
    // run a second one every 8 s on the same route; it now takes this list as a
    // prop instead. And nothing slowed when the tab was hidden, so a logged-in
    // tab nobody was looking at polled all night.
    //
    // Hidden tabs slow to once a minute rather than stopping. The chime exists
    // to alert someone who is NOT looking at Spinboard -- a tech in Lightspeed --
    // so stopping would silence it. Chrome already throttles hidden timers to
    // about a minute, so this mostly makes existing behaviour deliberate. Coming
    // back to the tab polls at once, so anything that arrived meanwhile chimes
    // and shows immediately. (A visible-but-unfocused window, e.g. a second
    // monitor, is not `hidden` and keeps the full cadence.)
    let timer = null;
    const tick = () => {
      poll();
      clearTimeout(timer);
      timer = setTimeout(tick, document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS);
    };
    const onVisibility = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisibility);
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUser, playChime, playPaymentChime]);

  // Per person. Saves to their account so it follows them across devices.
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('vsm-theme', next);
    if (currentUser) {
      fetch(`${API}/auth/users/me/preferences`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('sb_token')}` },
        body: JSON.stringify({ theme: next }),
      }).catch(() => {});
      try {
        const stored = JSON.parse(localStorage.getItem('sb_user'));
        if (stored) localStorage.setItem('sb_user', JSON.stringify({ ...stored, preferences: { ...(stored.preferences || {}), theme: next } }));
      } catch {}
    }
  };

  // Public booking page — no login, no Lightspeed auth gate. Must be checked
  // before the authStatus loading gate so customers never see app internals.
  // The staff onboarding console. Checked with the other public pages, above
  // the shop app's auth gate, because it has its OWN login — a shop token is
  // not a console token and vice versa.
  if (window.location.pathname === '/admin' || window.location.pathname === '/admin/') {
    return <AdminConsole />;
  }
  // The shop intake form: a prospect with only the private link from their
  // invite email (17 Oct). No login of either kind.
  if (/^\/onboard\/[A-Za-z0-9_-]+\/?$/.test(window.location.pathname)) {
    return <IntakeForm />;
  }
  // /booking/manage is checked first: it is a page, not a shop slug.
  if (window.location.pathname === '/booking/manage') {
    return <BookingManagePage />;
  }
  // /booking (older single-shop link), /booking/<shop-slug> and, for rentals
  // and demos, /booking/<shop-slug>/rentals all land here. BookingPage reads
  // the slug and the page out of the path itself.
  if (/^\/booking(\/[A-Za-z0-9_-]+)?(\/rentals)?\/?$/.test(window.location.pathname)) {
    return <BookingPage />;
  }
  if (window.location.pathname.startsWith('/pay')) {
    return <PaymentPage />;
  }
  // A rental's damage hold, texted to the customer (RentalHoldPage).
  if (window.location.pathname === '/rental-hold') {
    return <RentalHoldPage />;
  }
  // A quote, texted or emailed for the customer to approve (QuotePage).
  if (window.location.pathname === '/quote') {
    return <QuotePage />;
  }
  // "How did we do?", texted or emailed after a ticket is Done & Paid (FeedbackPage).
  if (window.location.pathname === '/feedback') {
    return <FeedbackPage />;
  }

// Each tab names the permission that reveals it. ROLE_DEFAULTS in
// server/helpers/auth.js decides who holds that permission — this list must
// never re-encode roles, which is exactly how the nav and the permission
// table came to disagree.
// Hidden only on an explicit false: the nav is UX, not a security boundary
// (the routes are), so a session that somehow arrives without a permission
// map renders a usable app rather than an empty sidebar.
const NAV_ITEMS = [
  { id:'start',     label:'Getting started',  icon:'ti-rocket',         roles:['owner'] },
  { id:'queue',     label:'Service Queue',    icon:'ti-layout-grid',    perm:'service_queue' },
  { id:'quotes',    label:'Quotes',           icon:'ti-file-invoice',   perm:'quotes' },
  { id:'parts',     label:'Parts to Order',   icon:'ti-truck-delivery', perm:'parts_order' },
  { id:'pickup',    label:'Ready for Pickup', icon:'ti-check',          perm:'pickup' },
  { id:'bookings',  label:'Bookings',         icon:'ti-calendar',       perm:'bookings' },
  { id:'payments',  label:'Payments',         icon:'ti-credit-card',    perm:'payments' },
  { id:'messages',  label:'Messages',         icon:'ti-message-circle', perm:'messages' },
  { id:'customers', label:'Customers',        icon:'ti-users',          perm:'customers' },
  // Shown once the shop has asked customers for feedback (reviewsActive), or
  // when opened from a low-score alert. `reviews` is NOT on by default for
  // techs and associates, so this one is hidden unless explicitly true.
  { id:'reviews',   label:'Reviews',          icon:'ti-star',           perm:'reviews', explicit:true },
  { id:'tech',      label:'My Workbench',     icon:'ti-tool',           perm:'tech_view' },
  // Settings is decided by ROLE, not by a permission key. `settings` is gone
  // from the permission table entirely (17 Sep): the screen carries the
  // Lightspeed and Stripe connections, the register keys and the shop's money
  // settings, and a per-user override was one API field away from handing all
  // of that to a tech. The server refuses the routes too -- this only keeps the
  // tab out of the sidebar.
  { id:'settings',  label:'Settings',         icon:'ti-settings',       roles:['owner','manager'] },
].filter(item => (item.roles ? item.roles.includes(currentUser?.role)
  : item.explicit ? currentUser?.permissions?.[item.perm] === true && (item.id !== 'reviews' || reviewsActive || nav === 'reviews')
  : currentUser?.permissions?.[item.perm] !== false))
  // Getting started stays in the menu until setup is done.
  .filter(item => item.id !== 'start' || (guide && (!guide.complete || nav === 'start')));

  // The Settings groups this person may see. Administrator-only groups are
  // absent for a Manager -- and the server refuses them as well.
  const settingsGroups = groupsFor(currentUser?.role, { websiteOn });

  // Every sidebar click goes through here. If a Settings group has unsaved
  // edits, the destination is parked and the dialog asks; otherwise the move
  // happens at once. Leaving the module loses the edits exactly as switching
  // group does, so both are guarded.
  const goTo = (target) => {
    if (dirtyGroup && !(target.nav === 'settings' && target.group === dirtyGroup)) {
      setPendingNav(target);
      return;
    }
    applyNav(target);
  };
  const applyNav = (target) => {
    // Quotes pressed while already on Quotes: back to the list (24 Oct).
    if (target.nav === 'quotes') setQuotesHome(n => n + 1);
    setNav(target.nav);
    if (target.nav === 'settings') setSettingsGroup(target.group || settingsGroups[0]?.id);
    setPendingNav(null);
  };

  // Fires when the save the dialog asked for has actually landed. The page
  // clears its dirty group on a SUCCESSFUL save only, so a failed save leaves
  // this waiting rather than navigating away from the edits it just lost.
  //
  // Held in a ref so the effect depends only on the two things that should
  // retrigger it. Naming applyNav in the deps would rebuild it every render;
  // omitting it needs a disable comment, and the rule that comment names is
  // not registered in the production build, which fails it (17 Sep).
  const applyNavRef = useRef(applyNav);
  applyNavRef.current = applyNav;
  useEffect(() => {
    if (leaveAfterSave && !dirtyGroup) {
      const target = leaveAfterSave;
      setLeaveAfterSave(null);
      applyNavRef.current(target);
    }
  }, [dirtyGroup, leaveAfterSave]);

  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const isResetPage = window.location.pathname === '/reset-password' && resetToken;

if (isResetPage) {
  return <ResetPassword token={resetToken} />;
}

  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
}

  // The Lightspeed status gate sits BELOW the login gate deliberately. It used
  // to sit above it, which meant a null authStatus withheld the LOGIN SCREEN --
  // so any path that failed to set a status locked everyone out of the app
  // entirely, showing "Loading…" forever. Signing out did exactly that on
  // Aug 25. Whether the shop has connected Lightspeed has no bearing on whether
  // someone can be shown a login form, and the two should never have been
  // sequenced so that the answer to one withheld the other.
  if (!authStatus) return (
    <div style={{ background:'#111827', display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
      <div style={{ color:'#5a6a8a' }}>Loading…</div>
    </div>
  );

  return (
    <div style={{ ...S.app, background: T.bg }}>
      <Toaster />

      {/* Session could not be verified. Blocking on purpose: the alternative
          is the screen everyone used to get, which looked like a working app
          with no work in it. Background polls keep running underneath and will
          keep re-triggering this until the cause clears, which is fine - the
          state is already set. */}
      {sessionIssue && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(8,12,22,0.72)',
                      display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background: T.sidebar, border:`1px solid ${T.border}`, borderRadius:12,
                        maxWidth:440, width:'100%', padding:'26px 28px', boxShadow:'0 18px 50px rgba(0,0,0,0.45)' }}>
            <div style={{ fontSize:17, fontWeight:700, color: T.text, marginBottom:10 }}>
              {sessionIssue === 'unavailable'
                ? "Spinboard can't reach its database"
                : "Spinboard couldn't verify your account"}
            </div>
            <div style={{ fontSize:14, color: T.textSub, lineHeight:1.55, marginBottom:22 }}>
              {sessionIssue === 'unavailable'
                ? "Nothing is wrong with your account and nothing you've done has been lost. This is usually brief \u2014 try again in a moment."
                : "Your sign-in hasn't expired, so this is usually a brief server problem. Try again in a moment. If it keeps happening, sign out and sign back in."}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button
                onClick={handleLogout}
                style={{ padding:'9px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                         background:'transparent', color: T.textSub, border:`1px solid ${T.border}` }}>
                Sign out
              </button>
              <button
                onClick={() => setSessionIssue(null)}
                style={{ padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                         background: T.btnPrimary, color: T.btnPrimaryText, border:`1px solid ${T.btnPrimary}` }}>
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ?? Sidebar ?? */}
      <div style={{ ...S.sidebar, background: '#0f1724', borderRight:'1px solid rgba(255,255,255,0.07)', overflowY:'visible', display:'flex', flexDirection:'column' }}>

        {/* Shop branding */}
        <div style={{ padding:'20px 20px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', marginBottom:8, textAlign:'center', flexShrink:0 }}>
          {shopProfile.logoUrl ? (
            <img src={shopProfile.logoUrl} alt="Shop logo"
              style={{ width:'100%', maxHeight:80, objectFit:'contain', marginBottom:10, display:'block' }}
              onError={e => e.target.style.display='none'}
            />
          ) : (
            <div style={{ width:48, height:48, background:'#243050', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 10px' }}>
              <SpinboardMark size={30} />
            </div>
          )}
          <div style={{ fontFamily:'Arial, sans-serif', fontSize:13, fontWeight:700, color:'#ccd0dc', lineHeight:1.3 }}>
            {shopProfile.name || 'Your Shop Name'}
          </div>
          <div style={{ fontSize:10, color:'#3a5070', marginTop:3, textTransform:'uppercase', letterSpacing:'0.06em' }}>
            Service Manager
          </div>
        </div>

      {/* Current user */}
      <div style={{ padding:'8px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', marginBottom:4, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:'#ccd0dc' }}>
       {currentUser.first_name && currentUser.last_name 
         ? `${currentUser.first_name} ${currentUser.last_name}`
         : currentUser.username}
     </div>
            <div style={{ fontSize:10, color:'#4a6080', textTransform:'uppercase', letterSpacing:'0.06em' }}>{roleLabel(currentUser.role)}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            style={{ background:'rgba(255,255,255,0.05)', border:'0.5px solid rgba(255,255,255,0.18)', borderRadius:5, cursor:'pointer', color:'#a0b0c8', fontSize:10, padding:'4px 10px', lineHeight:1, letterSpacing:'0.02em' }}
          >
            Sign out
          </button>
        </div>
      </div>

        {/* Every sidebar click goes through here. The unsaved-changes guard has
            to cover a click on Service Queue as much as one on another Settings
            group -- leaving the module loses the edits either way. */}
        {/* Nav — scrollable middle section */}
        <div style={{ flex:1, overflowY:'auto', padding:'8px 0' }}>
          {NAV_ITEMS.map(item => {
            const active = nav === item.id;
            return (
              <React.Fragment key={item.id}>
              <div
                style={{ display:'flex', alignItems:'center', gap:10,
                  padding:'10px 12px', margin:'1px 8px',
                  cursor:'pointer', borderRadius:7,
                  background: active ? 'rgba(230,126,34,0.16)' : 'transparent',
                  color: active ? '#e8e4dc' : '#4a6080',
                  fontWeight: active ? 500 : 400,
                  fontSize:13, transition:'all 0.15s', userSelect:'none',
                }}
                onClick={() => {
                  if (item.id === 'bookings' && newBookingCount > 0) {
                    // Clear immediately rather than waiting for the next poll —
                    // opening the module IS having seen them.
                    setNewBookingCount(0);
                    const token = localStorage.getItem('sb_token');
                    fetch(`${API}/book/staff/mark-viewed`, {
                      method: 'PUT', credentials: 'include',
                      headers: { Authorization: `Bearer ${token}` },
                    }).catch(() => {});
                  }
                  if (item.id === 'payments') {
                    // Freeze the watermark FIRST, before mark-viewed moves it.
                    // Get this order wrong and every "New" badge is stamped out
                    // by the very act of going to look at them.
                    setPaymentsNewSince(paymentsViewedAtRef.current || null);
                    if (newPaymentCount > 0) {
                      // Same reasoning as bookings: opening the module IS
                      // having seen them, so clear locally now instead of
                      // waiting up to 15 seconds for the next poll to agree.
                      setNewPaymentCount(0);
                      lastPaymentCountRef.current = 0;
                      const token = localStorage.getItem('sb_token');
                      fetch(`${API}/api/payments/mark-viewed`, {
                        method: 'PUT', credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` },
                      }).catch(() => {});
                    }
                  }
                  if (item.id === 'tech' && nav === 'tech' && techViewNavHandler?.fn) {
                    techViewNavHandler.fn();
                  } else if (item.id === 'settings') {
                    // Opening Settings lands on the group last open, list shown.
                    // Already inside it, the click folds or unfolds the list;
                    // the group being edited stays on screen either way.
                    if (nav !== 'settings') {
                      foldSettings(false);
                      goTo({ nav: 'settings', group: settingsGroup || settingsGroups[0]?.id });
                    } else {
                      foldSettings(!settingsFolded);
                    }
                  } else {
                    goTo({ nav: item.id });
                  }
                }}>
                <i className={`ti ${item.icon}`}
                  style={{ fontSize:17, color: active ? '#e67e22' : 'inherit', flexShrink:0 }}
                  aria-hidden="true" />
                <span style={{ flex:1 }}>{item.label}</span>
                {item.id === 'start' && guide && (
                  <span style={{ fontSize:11, color:'#e67e22', fontWeight:700, flexShrink:0 }}>{guide.doneCount}/{guide.requiredCount}</span>
                )}
                {item.id === 'messages' && hasFailedMessages && (
                  <i className="ti ti-alert-triangle" title="A message failed to deliver"
                    style={{ color:'#e74c3c', fontSize:16, flexShrink:0 }} aria-hidden="true" />
                )}
                {item.id === 'bookings' && newBookingCount > 0 && (
                  <span style={{ background:'#e67e22', color:'#fff', fontSize:11, fontWeight:700,
                    minWidth:18, height:18, borderRadius:9, padding:'0 5px',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {newBookingCount}
                  </span>
                )}
                {item.id === 'parts' && partsOrderCount > 0 && (
                  <span title={`${partsOrderCount} part${partsOrderCount !== 1 ? 's' : ''} to order`}
                    style={{ background:'#e67e22', color:'#fff', fontSize:11, fontWeight:700,
                    minWidth:18, height:18, borderRadius:9, padding:'0 5px',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {partsOrderCount}
                  </span>
                )}
                {item.id === 'reviews' && reviewsCount > 0 && (
                  <span title={`${reviewsCount} unhappy customer${reviewsCount !== 1 ? 's' : ''} waiting for a call`}
                    style={{ background:'#e74c3c', color:'#fff', fontSize:11, fontWeight:700,
                    minWidth:18, height:18, borderRadius:9, padding:'0 5px',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {reviewsCount}
                  </span>
                )}
                {item.id === 'quotes' && quoteAnswerCount > 0 && (
                  <span title={`${quoteAnswerCount} to look at: customers' answers, and tickets waiting for an estimate`}
                    style={{ background:'#27ae60', color:'#fff', fontSize:11, fontWeight:700,
                    minWidth:18, height:18, borderRadius:9, padding:'0 5px',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {quoteAnswerCount}
                  </span>
                )}
                {item.id === 'payments' && newPaymentCount > 0 && (
                  <span title={`${newPaymentCount} payment${newPaymentCount !== 1 ? 's' : ''} received`}
                    style={{ background:'#27ae60', color:'#fff', fontSize:11, fontWeight:700,
                    minWidth:18, height:18, borderRadius:9, padding:'0 5px',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {newPaymentCount}
                  </span>
                )}
                {item.id === 'messages' && unreadMsgCount > 0 && (
                  <span style={{ background:'#e74c3c', color:'#fff', fontSize:11, fontWeight:700,
                    minWidth:18, height:18, borderRadius:9, padding:'0 5px',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {unreadMsgCount}
                  </span>
                )}
                {item.id === 'settings' && nav === 'settings' && settingsFolded && dirtyGroup && (
                  <span title="Unsaved changes"
                    style={{ width:7, height:7, borderRadius:'50%', background:'#e67e22', flexShrink:0 }} />
                )}
                {item.id === 'settings' && (
                  <i className={`ti ti-chevron-${nav === 'settings' && !settingsFolded ? 'down' : 'right'}`}
                    style={{ fontSize:14, flexShrink:0, opacity:0.8 }} aria-hidden="true" />
                )}
              </div>
              {/* The Settings groups, indented under Settings while it is open.
                  Hidden when you leave the module, or when Settings is clicked
                  while open (folded); the sidebar keeps its usual length. */}
              {item.id === 'settings' && nav === 'settings' && !settingsFolded && (
                <div style={{ margin:'2px 8px 6px 30px', paddingLeft:10, borderLeft:'1px solid #243050' }}>
                  {settingsGroups.map(g => {
                    const here = settingsGroup === g.id;
                    return (
                      <div key={g.id}
                        onClick={() => goTo({ nav:'settings', group:g.id })}
                        style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px',
                          margin:'1px 0', borderRadius:6, cursor:'pointer', fontSize:12.5,
                          background: here ? 'rgba(230,126,34,0.16)' : 'transparent',
                          color: here ? '#e8e4dc' : '#4a6080', fontWeight: here ? 500 : 400,
                          userSelect:'none' }}>
                        <span style={{ flex:1 }}>{g.label}</span>
                        {dirtyGroup === g.id && (
                          <span title="Unsaved changes"
                            style={{ width:7, height:7, borderRadius:'50%', background:'#e67e22', flexShrink:0 }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
            );
          })}
        </div>

        {/* Bottom: wordmark + theme toggle — always visible, never scrolls */}
        <div style={{ padding:'10px 20px 16px', borderTop:'1px solid rgba(255,255,255,0.07)', flexShrink:0, background:'#0f1724' }}>
          <div style={{ textAlign:'center', marginBottom:10 }}>
            <div style={{ fontFamily:'Arial, sans-serif', fontSize:14, fontWeight:700, letterSpacing:'-0.5px', color:'#3a4a6a' }}>
              spinboard<span style={{ color:'#e67e22' }}>.</span>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontSize:11, color:'#3a4a6a' }}>Theme: {theme==='dark' ? 'Dark' : 'Light'}</span>
            <div onClick={toggleTheme}
              style={{ width:36, height:20, background: theme==='dark' ? '#243050' : '#e67e22', borderRadius:10, cursor:'pointer', position:'relative', transition:'all 0.2s' }}>
              <div style={{ width:16, height:16, background:'white', borderRadius:'50%', position:'absolute', top:2, left: theme==='dark' ? 2 : 18, transition:'all 0.2s' }} />
            </div>
          </div>
        </div>

      </div>

      {/* Unsaved changes. A real dialog rather than window.confirm: the browser
          one cannot offer three answers, and "Keep editing" is the one somebody
          reaches for when they clicked the wrong thing. Nothing is saved or
          discarded until they choose. */}
      {pendingNav && (
        <div
          role="dialog" aria-modal="true" aria-labelledby="unsaved-title"
          onClick={() => setPendingNav(null)}
          style={{ position:'fixed', inset:0, background:'rgba(10,14,22,0.55)', zIndex:200,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:12,
              padding:22, maxWidth:440, width:'100%', boxSizing:'border-box',
              boxShadow:'0 18px 50px rgba(0,0,0,0.45)' }}>
            <div id="unsaved-title" style={{ fontSize:16, fontWeight:700, color:T.text, marginBottom:6 }}>
              Unsaved changes
            </div>
            <div style={{ fontSize:13, color:T.textSub, marginBottom:18 }}>
              Your changes in Settings have not been saved. Save them before you leave?
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', flexWrap:'wrap' }}>
              <button style={{ ...S.btn(false), color:T.textSub, borderColor:T.filterBorder }}
                onClick={() => setPendingNav(null)}>Keep editing</button>
              <button style={{ ...S.btn(false), color:T.textSub, borderColor:T.filterBorder }}
                onClick={() => {
                  // The edits live in SettingsPage; unmounting it is the
                  // discard, and moving away does exactly that.
                  setDirtyGroup(null);
                  applyNav(pendingNav);
                }}>Discard</button>
              <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText }}
                onClick={() => {
                  // Ask SettingsPage to save, and go once it reports itself
                  // clean -- see the effect below. Navigating here instead
                  // would unmount the component mid-request.
                  setSaveSignal(n => n + 1);
                  setLeaveAfterSave(pendingNav);
                  setPendingNav(null);
                }}>Save and continue</button>
            </div>
          </div>
        </div>
      )}

      {/* ?? Main content ?? */}
      <div style={{ ...S.main, background: T.bg }}>
        {/* `unknown` means nobody was authenticated when we asked, which says
            nothing about whether the shop has Lightspeed — showing this banner
            then is what put "Not connected" over a connected shop. It also
            stops the banner flashing in the moment between logging in and the
            re-check returning. */}
        {/* Credentials are stored but Lightspeed is refusing them -- revoked,
            or the refresh chain died. Reconnect (same account) is the fix, and
            anyone on shift can do it, so the button is not owner-gated. Before
            18 Oct this state read as Connected and screens were simply empty. */}
        {authStatus.authenticated && authStatus.connectionBroken && (
          <div style={{ background: theme==='light' ? '#fdecea' : '#2a1618', border:`1px solid ${theme==='light' ? '#f5c2c0' : '#5c2a2e'}`, borderRadius:8, padding:'12px 18px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
            <div style={{ fontSize:13, color: theme==='light' ? '#a12a24' : '#f0a8a4' }}><strong>Lightspeed has stopped accepting Spinboard's connection</strong> — tickets, customers and payments won't load until it is reconnected. Reconnecting takes a minute and keeps the same Lightspeed account.</div>
            <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, flexShrink:0 }} onClick={() => startLightspeedConnect(false).catch(e => alert(e.message))}>Reconnect Lightspeed</button>
          </div>
        )}

        {/* Spinboard's own payment overdue (22 Oct). Warning first, naming the
            day; then read-only. The owner gets the button; everyone sees why. */}
        {currentUser && billingState.state !== 'ok' && (() => {
          const ro = billingState.state === 'read_only';
          const day = (iso) => new Date(iso).toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
          return (
            <div style={{ background: theme==='light' ? (ro ? '#fdecea' : '#fff8e1') : (ro ? '#2a1618' : '#1a1e2e'), border:`1px solid ${theme==='light' ? (ro ? '#f5c2c0' : '#f0d060') : (ro ? '#5c2a2e' : '#2e3d58')}`, borderRadius:8, padding:'12px 18px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
              <div style={{ fontSize:13, color: theme==='light' ? (ro ? '#a12a24' : '#8a6d0b') : (ro ? '#f0a8a4' : '#c9b46a') }}>
                {ro
                  ? <><strong>Spinboard is read-only</strong> because your Spinboard payment is overdue. You can see everything, but changes are paused until billing is updated. Customer texts and online booking keep working.</>
                  : <><strong>A Spinboard payment didn't go through.</strong> {billingState.readOnlyFrom ? <>Spinboard becomes read-only on {day(billingState.readOnlyFrom)} unless billing is updated.</> : <>Update billing to keep Spinboard fully working.</>}</>}
                {currentUser.role !== 'owner' && <> Let your shop's owner know.</>}
              </div>
              {currentUser.role === 'owner' && (
                <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, flexShrink:0 }} onClick={openBilling}>Update billing</button>
              )}
            </div>
          );
        })()}

        {!authStatus.authenticated && !authStatus.unknown && (
          <div style={{ background: theme==='light' ? '#fff8e1' : '#1a1e2e', border:`1px solid ${theme==='light' ? '#f0d060' : '#2e3d58'}`, borderRadius:8, padding:'12px 18px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            {/* Was "Demo mode — showing mock data", which stopped being true on
                Aug 14 when the mock fallback was deleted. There is no demo data
                any more: without Lightspeed there is simply nothing to show, and
                a banner claiming otherwise sends someone looking for a toggle
                that does not exist. */}
            <div style={{ fontSize:13, color: theme==='light' ? '#b8860b' : '#8a9ab8' }}><strong>Not connected to Lightspeed</strong> — tickets, customers and payments are unavailable until the shop is reconnected.</div>
            <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText }} onClick={() => startLightspeedConnect().catch(e => alert(e.message))}>Connect Lightspeed</button>
          </div>
        )}

        {nav === 'settings' && (
          <Settings
            theme={theme}
            authStatus={authStatus}
            currentUser={currentUser}
            group={settingsGroups.some(g => g.id === settingsGroup) ? settingsGroup : settingsGroups[0]?.id}
            onDirtyChange={setDirtyGroup}
            saveSignal={saveSignal}
          />
        )}
        {nav === 'start' && (
          <GettingStarted theme={theme} onStatus={setGuide} returnNotice={guideReturn}
            onOpenSettings={(group) => goTo({ nav: 'settings', group })}
            onFinish={() => {
              // "I will finish later": stop landing here for a day. The menu
              // item stays, so the guide is one click away.
              try { localStorage.setItem('sb_guide_snooze', String(Date.now() + 864e5)); } catch (_) {}
              goTo({ nav: 'queue' });
            }} />
        )}
        {nav === 'pickup'    && <PickupQueue   theme={theme} currentUser={currentUser} />}
        {nav === 'messages'  && <Messages      theme={theme} currentUser={currentUser} polledConversations={polledConversations} />}
        {nav === 'bookings'  && <Bookings      theme={theme} currentUser={currentUser} />}
        {nav === 'payments'  && <Payments      theme={theme} currentUser={currentUser} newSince={paymentsNewSince} />}
        {nav === 'customers' && <Customers     theme={theme} pendingCustomer={pendingCustomer} onPendingCustomerLoaded={() => setPendingCustomer(null)} currentUser={currentUser} />}
        {nav === 'tech'      && <TechView      theme={theme} onRegisterNavHandler={setTechViewNavHandler} currentUser={currentUser} />}
        {nav === 'queue'     && <ServiceQueue  theme={theme} currentUser={currentUser} />}
        {nav === 'parts'     && <PartsToOrder theme={theme} currentUser={currentUser} />}
        {nav === 'reviews'   && currentUser?.permissions?.reviews && <Reviews theme={theme} />}
        {nav === 'quotes'    && <Quotes theme={theme} currentUser={currentUser} openQuoteId={pendingQuote && pendingQuote.id} startNew={!!(pendingQuote && pendingQuote.isNew)} onOpened={clearPendingQuote} homeSignal={quotesHome} />}
      </div>
    </div>
  );
}
